import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Download, ImageIcon, Loader2, RefreshCcw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";

const PRESETS = [
  ["BrightIQ Blue", "#1F5BFF"], ["Cyan", "#17B6E0"], ["Green", "#3AA64B"],
  ["Amber", "#F5A524"], ["Pink", "#E84393"], ["Purple", "#7C3AED"],
];
const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all";

export default function ReportThemeSettings() {
  const [theme, setTheme] = useState(null);
  const [accent, setAccent] = useState("#1F5BFF");
  const [logoUrl, setLogoUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    const res = await authFetch("/api/admin/report-theme");
    if (!res.ok) { toast.error("Could not load report theme"); return; }
    const data = await res.json();
    setTheme(data);
    setAccent(data.accent_color || "#1F5BFF");
    if (data.has_logo) {
      const logo = await authFetch("/api/admin/report-theme/logo");
      if (logo.ok) {
        const blob = await logo.blob();
        setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
      }
    } else {
      setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (logoUrl) URL.revokeObjectURL(logoUrl); }, [logoUrl]);

  const saveAccent = async () => {
    setBusy(true);
    const res = await authFetch("/api/admin/report-theme", { method: "PUT", body: JSON.stringify({ accent_color: accent }) });
    const data = await res.json();
    setBusy(false);
    if (!res.ok) { toast.error(data.detail || "Could not save theme"); return; }
    setTheme(data);
    toast.success("Report accent saved");
  };

  const uploadLogo = async () => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    const res = await authFetch("/api/admin/report-theme/logo", { method: "POST", body: form });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { toast.error(data.detail || "Logo upload failed"); return; }
    setFile(null);
    if (fileInput.current) fileInput.current.value = "";
    await load();
    toast.success("Report logo uploaded");
  };

  const removeLogo = async () => {
    setBusy(true);
    const res = await authFetch("/api/admin/report-theme/logo", { method: "DELETE" });
    setBusy(false);
    if (res.ok) { await load(); toast.success("Report logo removed"); }
  };

  const resetTheme = async () => {
    setBusy(true);
    const res = await authFetch("/api/admin/report-theme/reset", { method: "POST" });
    setBusy(false);
    if (res.ok) { setFile(null); await load(); toast.success("Theme reset to BrightIQ defaults"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-theme-settings">
      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-6">
        <section className="rounded-3xl bg-cream border border-ink/10 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-xl tracking-tight">Report theme</h3>
              <p className="text-xs text-ink/50 mt-1">One site-wide accent and logo applied to every exported PDF.</p>
            </div>
            <span className="rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: `${accent}18`, color: accent }}>Live</span>
          </div>

          <div className="mt-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/45 mb-3">Accent presets</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PRESETS.map(([name, hex]) => (
                <button key={hex} type="button" onClick={() => setAccent(hex)} data-testid={`preset-color-${name.toLowerCase().replace(/\s+/g, "-")}`} className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${accent.toUpperCase() === hex ? "border-ink bg-white" : "border-ink/10 bg-white/60 hover:border-ink/30"}`}>
                  <span className="h-6 w-6 rounded-full border border-ink/10" style={{ background: hex }} />
                  <span className="text-xs font-semibold">{name}</span>
                  {accent.toUpperCase() === hex && <Check className="ml-auto h-4 w-4" />}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-[110px_1fr_auto] gap-3 items-end">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/45 block mb-2">Picker</span>
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value.toUpperCase())} data-testid="accent-color-picker" className="h-11 w-full rounded-xl border border-ink/15 bg-white cursor-pointer" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/45 block mb-2">Hex</span>
              <input value={accent} onChange={(e) => setAccent(e.target.value.toUpperCase())} data-testid="accent-hex-input" className={inputCls} />
            </label>
            <button onClick={saveAccent} disabled={busy || !/^#[0-9A-F]{6}$/.test(accent)} data-testid="save-theme-button" className="h-11 bg-ink text-paper text-xs font-semibold px-6 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save accent"}</button>
          </div>
        </section>

        <section className="rounded-3xl bg-cream border border-ink/10 p-6">
          <h3 className="font-display font-bold text-xl tracking-tight">Institution logo</h3>
          <p className="text-xs text-ink/50 mt-1">PNG, JPG or WEBP under 2 MB. It appears on PDF headers and cover pages.</p>
          <div className="mt-5 h-28 rounded-2xl bg-white border border-ink/10 flex items-center justify-center p-4 overflow-hidden" data-testid="pdf-live-preview-container">
            <img src={logoUrl || "/brightiq-logo.png"} alt="Report logo preview" data-testid="logo-preview-image" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="logo-upload-input" className="text-xs file:mr-3 file:rounded-full file:border-0 file:bg-ink file:text-paper file:px-4 file:py-2 file:text-xs file:font-semibold" />
            <button onClick={uploadLogo} disabled={!file || busy} data-testid="upload-logo-button" className="flex items-center gap-2 bg-ink text-paper text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors"><Upload className="h-3.5 w-3.5" /> Upload</button>
            {theme?.has_logo && <button onClick={removeLogo} disabled={busy} data-testid="remove-logo-button" className="flex items-center gap-2 border border-iqpink/30 text-iqpink text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-iqpink hover:text-paper disabled:opacity-60 transition-colors"><Trash2 className="h-3.5 w-3.5" /> Remove</button>}
          </div>
          {theme?.logo_filename && <p className="mt-3 text-xs text-ink/45">Current: {theme.logo_filename}</p>}
        </section>
      </div>

      <section className="rounded-3xl bg-white border border-ink/10 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between border-b border-ink/10">
          <div><h3 className="font-display font-bold text-lg tracking-tight">PDF header preview</h3><p className="text-xs text-ink/45">Live placement and accent treatment.</p></div>
          <button onClick={resetTheme} disabled={busy} data-testid="reset-theme-button" className="flex items-center gap-2 border border-ink/15 text-xs font-semibold px-4 py-2.5 rounded-full hover:bg-ink hover:text-paper transition-colors"><RefreshCcw className="h-3.5 w-3.5" /> Reset</button>
        </div>
        <div className="p-6 bg-paper">
          <div className="max-w-2xl mx-auto rounded-2xl bg-white border border-ink/10 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 pb-4 border-b-2" style={{ borderColor: accent }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink/45">Bright IQ · Psychometric Assessment</div>
              <img src={logoUrl || "/brightiq-logo.png"} alt="PDF header logo" className="h-10 max-w-28 object-contain" />
            </div>
            <div className="mt-6 rounded-xl p-4" style={{ background: `${accent}10`, borderLeft: `4px solid ${accent}` }}>
              <div className="font-display font-bold">Introduction</div>
              <p className="text-xs text-ink/55 mt-1">Accent color is used for section cues, score highlights and report details.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-3xl bg-iqblue/5 border border-iqblue/15 p-5 flex items-start gap-3">
        <Download className="h-5 w-5 text-iqblue shrink-0 mt-0.5" />
        <p className="text-sm text-ink/65">Theme changes apply to newly downloaded classic and Discovery PDFs immediately. Previously downloaded files remain unchanged.</p>
      </div>
    </div>
  );
}
